import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import type { UserRole } from '@/stores/appStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Building2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { currencySymbols } from '@/types';

export default function OrganizationSelect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizations, setCurrentOrganization } = useAppStore();

  const handleSelectOrganization = (org: typeof organizations[0]) => {
    if (org.organization) {
      setCurrentOrganization(org.organization, org.role as UserRole);
      toast.success(`Switched to ${org.organization.name}`);
      navigate('/dashboard');
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">{t('organization.select')}</h1>
        <p className="text-muted-foreground">
          Choose an organization to manage
        </p>
      </div>

      <div className="space-y-4">
        {organizations.map((org) => (
          <Card 
            key={org.id}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => handleSelectOrganization(org)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg truncate">{org.organization?.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="capitalize bg-secondary px-2 py-0.5 rounded text-xs">
                      {org.role}
                    </span>
                    <span>•</span>
                    <span>
                      {org.organization?.currency ? currencySymbols[org.organization.currency as keyof typeof currencySymbols] : '$'}
                      {org.organization?.monthly_dues_amount || 0} monthly
                    </span>
                  </div>
                </div>
                <Button variant="ghost" size="icon">
                  <Check className="w-5 h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow border-dashed"
          onClick={() => navigate('/organizations/create')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center flex-shrink-0">
                <Plus className="w-6 h-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{t('organization.create')}</h3>
                <p className="text-sm text-muted-foreground">
                  Create a new organization
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
